/**
 * The event log, as a React island rendered from `@annwn/ui`'s `LogPanel`
 * (RC1-05).
 *
 * The second live client surface to move onto the component library, after the
 * condition overlay — and the one M6-05 named as the deferred follow-up. The
 * split is the same: the interesting rules (the scrollback cap, the reader-aware
 * classification, the prominence filter, the battle grouping) stay in the pure,
 * tested `LogBuffer`; React owns nothing but turning the shown lines into DOM.
 *
 * The imperative handle is deliberately the same shape the old `LogPane` gave
 * `main.ts` — `append`, `note`, `setViewer`, `battle` — so the entry point calls
 * it exactly as before and nothing but the rendering changed. The hover-to-
 * highlight hook (M2-06) is preserved through `LogPanel`'s `onHighlight`: the
 * line still carries `actors`, and pointing at it still lights the grid.
 */

import { createRoot, type Root } from 'react-dom/client';

import { LogPanel, type LogEntry } from '@annwn/ui';

import { audible, LogBuffer, type LogClass, type LogLine, SCROLLBACK_LIMIT } from './log-pane.js';
import { toEntry } from './log-view-model.js';
import type { LogEvent } from '@annwn/protocol';

export interface LogOverlayOptions {
  /** The pointer is over a line about these actors, or has left one (M2-06). */
  onHighlight?(actors: readonly string[]): void;
}

export interface LogOverlayHandle {
  append(events: readonly LogEvent[]): void;
  note(text: string, tick?: number, kind?: LogClass): void;
  setViewer(actorId: string): void;
  /** The current or last fight, unabridged — what the battle view reads. */
  battle(): readonly LogLine[];
  destroy(): void;
}

/**
 * Mount the log island and return the imperative handle `main.ts` drives.
 *
 * `entries` is the shown, capped view the panel renders; the `LogBuffer` behind
 * it keeps the whole scrollback for the battle view. Appending only re-renders
 * when a line actually clears the prominence filter — a burst of other people's
 * small change costs nothing on screen, exactly as the imperative pane arranged.
 */
export function mountLogOverlay(
  container: HTMLElement,
  options: LogOverlayOptions = {},
): LogOverlayHandle {
  const root: Root = createRoot(container);
  const buffer = new LogBuffer();
  let entries: LogEntry[] = [];

  const render = (): void => {
    root.render(<LogPanel entries={entries} onHighlight={options.onHighlight} label="Event log" />);
  };

  const show = (added: readonly LogLine[]): void => {
    const shown = added.filter(audible);
    if (shown.length === 0) return;

    // Append and cap, rather than rebuild: the panel follows new lines on its
    // own, and a growing array bounded by the same scrollback the buffer keeps
    // is what stops a long session from laying out ten thousand paragraphs.
    entries = entries.concat(shown.map(toEntry)).slice(-SCROLLBACK_LIMIT);
    render();
  };

  render();

  return {
    append(events) {
      show(buffer.append(events));
    },
    note(text, tick = 0, kind: LogClass = 'system') {
      show([buffer.note(text, tick, kind)]);
    },
    setViewer(actorId) {
      buffer.viewerId = actorId;
    },
    battle() {
      return buffer.battle();
    },
    destroy() {
      root.unmount();
    },
  };
}
